import React, { useState, useEffect, useCallback } from 'react';
// import HumanitarianContext from './HumanitarianContext';
// import LiveDataSources from './LiveDataSources';
import DashboardMap from './DashboardMap';
import { Crisis } from '../types';
import { cn, isMobile, formatNumber, formatRelativeTime } from '../lib/utils';
import { SwissIcons } from './icons/SwissIconSystem';
import { useTheme } from 'next-themes';
import { getCrisisSeverityClass, getSwissStatusClass, createSwissThemeUtils, humanitarianUtils } from '../utils/swiss-theme-utils';
import '../styles/dashboard-grid.css';

interface DashboardProps {
  onCrisisSelect: (crisis: Crisis) => void;
  onMakeDecision: (crisis: Crisis) => void;
}

interface MockCrisis {
  id: number;
  title: string;
  type: string;
  status: string;
  severity: number;
  location: string;
  affected_population: number;
  created_at: string;
  lastUpdate: string;
}

interface MockStats {
  activeCrises?: number;
  affectedPopulation?: number;
  responsesActive?: number;
  alertsToday?: number;
  totalCrises?: number;
  totalDecisions?: number;
  averageConfidence?: number;
}

// Timeline event interface
interface TimelineEvent {
  id: string;
  timestamp: string;
  type: 'alert' | 'decision' | 'response' | 'update' | 'incident';
  title: string;
  description: string;
  severity?: number;
  actor?: string;
}

// Resource interface
interface Resource {
  type: string;
  available: number;
  deployed: number;
  status: 'adequate' | 'low' | 'critical';
  utilization: number;
}

// Decision interface
interface Decision {
  id: string;
  title: string;
  crisis: string;
  priority: 'high' | 'medium' | 'low';
  deadline: string;
  status: 'pending' | 'in_progress' | 'completed';
}

// Alert interface
interface Alert {
  id: string;
  type: string;
  location: string;
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  time: string;
  confidence: number;
}

const Dashboard: React.FC<DashboardProps> = ({ onCrisisSelect, onMakeDecision }) => {
  const { theme } = useTheme();
  const [crises, setCrises] = useState<MockCrisis[]>([]);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({});
  
  // Create theme-aware utilities
  const themeUtils = createSwissThemeUtils(theme as any);
  
  // Helper function to toggle section expansion
  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Get timeline event icon
  const getTimelineIcon = (type: string) => {
    switch (type) {
      case 'alert': return <SwissIcons.Emergency size={16} className="swiss-icon-emergency" />;
      case 'decision': return <SwissIcons.Approved size={16} className="swiss-icon-success" />;
      case 'response': return <SwissIcons.Supplies size={16} className="swiss-icon-info" />;
      case 'update': return <SwissIcons.Document size={16} className="swiss-icon-info" />;
      case 'incident': return <SwissIcons.Urgent size={16} className="swiss-icon-warning" />;
      default: return <SwissIcons.Alert size={16} className="swiss-icon-neutral" />;
    }
  };

  // Swiss theme-aware utility functions
  const getSeverityColorClass = (severity: number): string => {
    return getCrisisSeverityClass(severity, theme as any);
  };

  const getSeverityText = (severity: number): string => {
    const levels: Record<number, string> = {
      1: 'Low', 2: 'Minor', 3: 'Medium', 4: 'High', 5: 'Critical'
    };
    return levels[severity] || 'Unknown';
  };

  const getStatusColor = (status: string): string => {
    return getSwissStatusClass(status, theme as any);
  };

  // Get resource status color
  const getResourceStatusColor = (status: string) => {
    if (theme === 'swiss') {
      switch (status) {
        case 'adequate': return 'swiss-status-success';
        case 'low': return 'swiss-status-warning';
        case 'critical': return 'swiss-status-error';
        default: return 'swiss-text-secondary';
      }
    }
    
    // Fallback to CLEAR theme
    switch (status) {
      case 'adequate': return 'bg-sage-100 text-sage-700';
      case 'low': return 'bg-sand-100 text-sand-700';
      case 'critical': return 'bg-accent-100 text-accent-700';
      default: return 'bg-ink-100 text-ink-700';
    }
  };

  // Get decision priority color
  const getDecisionPriorityColor = (priority: string) => {
    if (theme === 'swiss') {
      switch (priority) {
        case 'high': return 'swiss-crisis-badge-critical';
        case 'medium': return 'swiss-crisis-badge-moderate';
        case 'low': return 'swiss-crisis-badge-minimal';
        default: return 'swiss-text-secondary';
      }
    }
    
    // Fallback to CLEAR theme
    switch (priority) {
      case 'high': return 'bg-accent-100 text-accent-700 border-accent-300';
      case 'medium': return 'bg-ocean-100 text-ocean-700 border-ocean-300';
      case 'low': return 'bg-sage-100 text-sage-700 border-sage-300';
      default: return 'bg-ink-100 text-ink-700 border-ink-300';
    }
  };

  // Get alert severity color
  const getAlertSeverityColor = (severity: string) => {
    if (theme === 'swiss') {
      switch (severity) {
        case 'Critical': return 'swiss-bg-crisis-critical';
        case 'High': return 'swiss-bg-crisis-high';
        case 'Medium': return 'swiss-bg-crisis-moderate';
        case 'Low': return 'swiss-bg-crisis-low';
        default: return 'swiss-bg-secondary';
      }
    }
    
    // Fallback to CLEAR theme
    switch (severity) {
      case 'Critical': return 'bg-accent-500';
      case 'High': return 'bg-accent-400';
      case 'Medium': return 'bg-ocean-400';
      case 'Low': return 'bg-sage-400';
      default: return 'bg-ink-400';
    }
  };

  const [stats, setStats] = useState<MockStats>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isMobileView, setIsMobileView] = useState(isMobile());
  const [isTabletView, setIsTabletView] = useState(false);
  
  // Handle window resize for responsive views
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobileView(width < 768);
      setIsTabletView(width >= 768 && width < 1200);
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = useCallback(async (): Promise<void> => {
    try {
      const [crisesResponse, statsResponse] = await Promise.all([
        fetch('/api/crises'),
        fetch('/api/dashboard/stats')
      ]);
      
      if (crisesResponse.ok && statsResponse.ok) {
        const crisesContentType = crisesResponse.headers.get('content-type');
        const statsContentType = statsResponse.headers.get('content-type');
        
        if (crisesContentType?.includes('application/json') && statsContentType?.includes('application/json')) {
          const crisesData: MockCrisis[] = await crisesResponse.json();
          const statsData: MockStats = await statsResponse.json();
          
          setCrises(crisesData);
          setStats(statsData);
        }
      } else {
        const mockCrises: MockCrisis[] = [
          {
            id: 1, title: "Flooding in Bangladesh", type: "Natural Disaster", 
            status: "active", severity: 4, location: "Dhaka, Bangladesh",
            affected_population: 250000, created_at: "2024-01-15T10:00:00Z", 
            lastUpdate: "2024-01-15T14:30:00Z"
          },
          {
            id: 2, title: "Syrian Refugee Crisis", type: "Conflict", 
            status: "ongoing", severity: 5, location: "Turkey-Syria Border",
            affected_population: 1200000, created_at: "2024-01-10T08:00:00Z", 
            lastUpdate: "2024-01-15T12:00:00Z"
          },
          {
            id: 3, title: "Food Insecurity in Somalia", type: "Famine", 
            status: "critical", severity: 4, location: "Mogadishu, Somalia",
            affected_population: 800000, created_at: "2024-01-12T15:00:00Z", 
            lastUpdate: "2024-01-15T09:15:00Z"
          }
        ];

        const mockStats: MockStats = {
          activeCrises: mockCrises.length,
          affectedPopulation: mockCrises.reduce((sum, crisis) => sum + crisis.affected_population, 0),
          responsesActive: 12,
          alertsToday: 8,
          totalCrises: 47,
          totalDecisions: 156,
          averageConfidence: 87
        };

        setCrises(mockCrises);
        setStats(mockStats);
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Displaying sample data.');
      
      const mockCrises: MockCrisis[] = [
        {
          id: 1, title: "Flooding in Bangladesh", type: "Natural Disaster", 
          status: "active", severity: 4, location: "Dhaka, Bangladesh",
          affected_population: 250000, created_at: "2024-01-15T10:00:00Z", 
          lastUpdate: "2024-01-15T14:30:00Z"
        }
      ];

      const mockStats: MockStats = {
        activeCrises: 1, affectedPopulation: 250000, responsesActive: 3,
        alertsToday: 2, totalCrises: 15, totalDecisions: 45, averageConfidence: 85
      };

      setCrises(mockCrises);
      setStats(mockStats);
    } finally {
      setLoading(false);
    }
  }, []);

  // Generate mock data when needed
  useEffect(() => {
    // Generate timeline data
    const mockTimeline: TimelineEvent[] = [
      {
        id: '1', timestamp: '2024-01-15T14:30:00Z', type: 'alert',
        title: 'Flood Alert Upgraded', description: 'Severity increased from medium to high based on rainfall data',
        severity: 4, actor: 'CLEAR AI System'
      },
      {
        id: '2', timestamp: '2024-01-15T13:15:00Z', type: 'decision',
        title: 'Emergency Response Activated', description: 'Deployed rapid response team to affected areas',
        actor: 'Crisis Manager'
      },
      {
        id: '3', timestamp: '2024-01-15T12:00:00Z', type: 'response',
        title: 'Medical Supplies Dispatched', description: '500 medical kits sent to Bangladesh relief centers',
        actor: 'Logistics Team'
      }
    ];

    // Generate resource data
    const mockResources: Resource[] = [
      { type: 'Medical Supplies', available: 850, deployed: 150, status: 'adequate', utilization: 15 },
      { type: 'Emergency Personnel', available: 45, deployed: 23, status: 'low', utilization: 51 },
      { type: 'Transport Vehicles', available: 12, deployed: 8, status: 'critical', utilization: 67 },
      { type: 'Shelter Materials', available: 200, deployed: 75, status: 'adequate', utilization: 38 }
    ];

    // Generate decision data
    const mockDecisions: Decision[] = [
      {
        id: '1', title: 'Evacuate coastal villages', crisis: 'Bangladesh Flooding',
        priority: 'high', deadline: '2024-01-16T08:00:00Z', status: 'in_progress'
      },
      {
        id: '2', title: 'Set up temporary shelters', crisis: 'Bangladesh Flooding',
        priority: 'high', deadline: '2024-01-16T12:00:00Z', status: 'pending'
      },
      {
        id: '3', title: 'Coordinate with local authorities', crisis: 'Syrian Refugee Crisis',
        priority: 'medium', deadline: '2024-01-17T10:00:00Z', status: 'pending'
      }
    ];

    // Generate alert data
    const mockAlerts: Alert[] = [
      {
        id: '1', type: 'Flood Warning', location: 'Bangladesh',
        severity: 'High', time: '2024-01-15T14:30:00Z', confidence: 92
      },
      {
        id: '2', type: 'Disease Outbreak Risk', location: 'Somalia',
        severity: 'Critical', time: '2024-01-15T13:45:00Z', confidence: 78
      },
      {
        id: '3', type: 'Supply Shortage', location: 'Turkey-Syria Border',
        severity: 'Medium', time: '2024-01-15T12:20:00Z', confidence: 85
      }
    ];

    setTimeline(mockTimeline);
    setResources(mockResources);
    setDecisions(mockDecisions);
    setAlerts(mockAlerts);
  }, []);

  // Mock severity color class for migration
  const getMockSeverityColorClass = (severity: number): string => {
    if (theme === 'swiss') {
      return getCrisisSeverityClass(severity, theme as any);
    }
    
    // Fallback to original CLEAR theme colors
    if (severity >= 5) return 'border-warm-200 bg-warm-50 text-warm-800';
    if (severity >= 4) return 'border-warm-300 bg-warm-100 text-warm-800';
    if (severity >= 3) return 'border-warm-200 bg-warm-50 text-warm-700';
    if (severity >= 2) return 'border-cool-200 bg-cool-50 text-cool-800';
    return 'border-cool-200 bg-cool-50 text-cool-800';
  };

  // Utility function for theme-aware icons
  const getThemeIcon = (iconName: string, size: number = 16) => {
    const iconClass = theme === 'swiss' ? "swiss-icon-neutral" : "w-4 h-4 text-slate-600";
    
    switch (iconName) {
      case 'person': return <SwissIcons.Users size={size} className={iconClass} />;
      case 'heart': return <SwissIcons.Health size={size} className={iconClass} />;
      case 'shield': return <SwissIcons.Protection size={size} className={iconClass} />;
      case 'trending-up': return <SwissIcons.TrendAnalysis size={size} className={iconClass} />;
      default: return <SwissIcons.Analytics size={size} className={iconClass} />;
    }
  };

  // Crisis status utility
  const getCrisisStatusClass = (status: string) => {
    if (theme === 'swiss') {
      return getSwissStatusClass(status, theme as any);
    }
    
    // Fallback to CLEAR theme
    switch (status?.toLowerCase()) {
      case 'active': return 'text-red-700 bg-red-100';
      case 'monitoring': return 'text-amber-700 bg-amber-100';
      case 'resolved': return 'text-green-700 bg-green-100';
      case 'emerging': return 'text-orange-700 bg-orange-100';
      default: return 'text-slate-600 bg-gray-100';
    }
  };

  // Get theme-aware container and component classes
  const getContainerClass = () => {
    if (theme === 'swiss') {
      return 'min-h-screen swiss-bg-primary';
    }
    return 'min-h-screen bg-gray-50';
  };

  const getCardClass = () => {
    return themeUtils.getCardClass();
  };

  const getTextClass = (variant: 'primary' | 'secondary' | 'tertiary' | 'muted' = 'primary') => {
    return themeUtils.getTextClass(variant);
  };

  const getButtonClass = (variant: 'primary' | 'secondary' | 'danger' = 'primary') => {
    return themeUtils.getButtonClass(variant);
  };

  const safeStats = stats || {};

  if (loading) {
    const loadingContainerClass = theme === 'swiss' 
      ? 'min-h-screen swiss-bg-primary flex items-center justify-center' 
      : 'min-h-screen bg-gray-50 flex items-center justify-center';
    
    const spinnerClass = theme === 'swiss' 
      ? 'animate-spin h-16 w-16 swiss-border-primary mx-auto'
      : 'animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto';
    
    const loadingTextClass = theme === 'swiss' 
      ? 'mt-4 swiss-text-secondary text-lg' 
      : 'mt-4 text-slate-600 text-lg';

    return (
      <div className={loadingContainerClass}>
        <div className="text-center">
          <div className={spinnerClass} />
          <p className={loadingTextClass}>Loading humanitarian dashboard...</p>
        </div>
      </div>
    );
  }

  if (isMobileView) {
    const errorClass = theme === 'swiss' 
      ? 'mx-4 mt-4 p-3 swiss-border swiss-bg-warning swiss-text-warning' 
      : 'mx-4 mt-4 p-3 border border-amber-200 bg-amber-50 rounded-lg';

    return (
      <div className={getContainerClass()}>
        <div className="p-4">
          <div className="mb-4">
            <h1 className={cn(getTextClass('primary'), "text-xl font-bold mb-2 wordmark")}>CLEAR Dashboard</h1>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className={cn(getTextClass('primary'), "font-bold", theme === 'swiss' ? 'swiss-text-accent-orange' : 'text-orange-600')}>
                  {formatNumber(safeStats.activeCrises || 0)}
                </div>
                <div className={getTextClass('secondary')}>Active</div>
              </div>
              <div>
                <div className={cn(getTextClass('primary'), "font-bold", theme === 'swiss' ? 'swiss-text-accent-red' : 'text-red-600')}>
                  {formatNumber(safeStats.affectedPopulation || 0)}
                </div>
                <div className={getTextClass('secondary')}>Affected</div>
              </div>
              <div>
                <div className={cn(getTextClass('primary'), "font-bold", theme === 'swiss' ? 'swiss-text-accent-blue' : 'text-blue-600')}>
                  {safeStats.averageConfidence || 0}%
                </div>
                <div className={getTextClass('secondary')}>Confidence</div>
              </div>
            </div>
          </div>

          {error && (
            <div className={errorClass}>
              <p className="text-sm">{error}</p>
            </div>
          )}

          <div className={cn(getCardClass(), "mb-4")}>
            <div className={cn("px-4 py-3", theme === 'swiss' ? 'swiss-border-b' : 'border-b border-gray-200')}>
              <h2 className={cn(getTextClass('primary'), "text-lg font-semibold")}>Global Crisis Map</h2>
            </div>
            <div className="p-4">
              <DashboardMap 
                crises={crises.map(crisis => ({
                  ...crisis,
                  confidence: 0.85,
                  evidence: [],
                  coordinates: [0, 0]
                }))} 
                onCrisisSelect={onCrisisSelect} 
              />
            </div>
            <div className={cn("p-4", theme === 'swiss' ? 'swiss-bg-warning swiss-border' : 'bg-coral-50 border border-coral-200 rounded-lg')}>
              <div className="flex items-center gap-2 mb-2">
                <div className={cn("w-2 h-2 animate-pulse", theme === 'swiss' ? 'swiss-bg-warning' : 'bg-coral-500 rounded-full')} />
                <h3 className={cn("text-sm font-medium", theme === 'swiss' ? 'swiss-text-warning' : 'text-coral-800')}>Priority Alerts</h3>
              </div>
              <p className={cn("text-xs mb-2", theme === 'swiss' ? 'swiss-text-warning' : 'text-coral-600')}>
                {alerts.length} active alerts requiring immediate attention
              </p>
              <button 
                className={cn("text-xs px-3 py-1 transition-colors", 
                  theme === 'swiss' ? 'swiss-button swiss-button-primary' : 'bg-coral-600 text-white rounded-md hover:bg-coral-700'
                )}
                onClick={() => {/* Navigate to alerts */}}
              >
                View All Alerts
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <h2 className={cn(getTextClass('primary'), "text-lg font-semibold mb-3")}>Active Crises</h2>
            {crises.map((crisis) => (
              <div key={crisis.id} className={cn(getCardClass(), "p-4")}>
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className={cn(getTextClass('primary'), "font-semibold text-sm leading-tight")}>
                        {crisis.title}
                      </h3>
                    </div>
                  </div>
                  <div className={cn(getTextClass('secondary'), "text-xs space-y-1")}>
                    <div className="flex justify-between">
                      <span>Type:</span>
                      <span className={getCrisisStatusClass(crisis.type)}>{crisis.type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Severity:</span>
                      <span className={cn("inline-block px-2 py-1 text-xs font-medium border", getSeverityColorClass(crisis.severity))}>
                        {getSeverityText(crisis.severity)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Location:</span>
                      <span>{crisis.location}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Affected:</span>
                      <span>{humanitarianUtils.formatPopulationCount(crisis.affected_population)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Updated:</span>
                      <span>{formatRelativeTime(crisis.lastUpdate)}</span>
                    </div>
                  </div>
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => onMakeDecision({
                        ...crisis,
                        confidence: 0.85,
                        evidence: [],
                        coordinates: [0, 0]
                      })}
                      className={cn("flex-1 px-3 py-2 text-sm font-medium transition-colors", 
                        theme === 'swiss' ? 'swiss-button swiss-button-primary' : 'bg-accent-500 text-white rounded-md hover:bg-accent-600'
                      )}
                    >
                      Make Decision
                    </button>
                    <button
                      onClick={() => onCrisisSelect({
                        ...crisis,
                        confidence: 0.85,
                        evidence: [],
                        coordinates: [0, 0]
                      })}
                      className={cn("flex-1 px-3 py-2 text-sm font-medium transition-colors",
                        theme === 'swiss' ? 'swiss-button' : 'border border-accent-500 text-accent-700 rounded-md hover:bg-accent-50'
                      )}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {crises.length === 0 && (
              <div className={cn(getCardClass(), "text-center py-12")}>
                <div className="space-y-3">
                  <svg className={cn("w-16 h-16 mx-auto", getTextClass('muted'))} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <p className={getTextClass('secondary')}>No active crises at the moment</p>
                  <button 
                    onClick={() => {/* Handle refresh or navigation */}}
                    className={cn("mt-3 text-sm transition-colors", 
                      theme === 'swiss' ? 'swiss-text-accent-blue hover:swiss-text-accent-blue' : 'text-accent-600 hover:text-accent-700'
                    )}
                  >
                    Refresh Dashboard
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Floating Action Button */}
        <button
          className={cn("fixed bottom-6 right-6 w-12 h-12 shadow-lg transition-colors flex items-center justify-center",
            theme === 'swiss' ? 'swiss-button swiss-button-primary' : 'bg-accent-500 text-white rounded-full hover:bg-accent-600'
          )}
          onClick={() => {/* Handle emergency action */}}
        >
          <SwissIcons.Emergency size={20} />
        </button>
      </div>
    );
  }

  // Desktop/Tablet View
  const headerClass = theme === 'swiss' 
    ? 'swiss-bg-primary swiss-border-b px-6 py-4' 
    : 'bg-white border-b border-gray-200 px-6 py-4';

  const titleClass = theme === 'swiss' 
    ? 'swiss-h2 wordmark' 
    : 'text-2xl font-bold text-neutral-900 wordmark';

  const buttonPrimaryClass = theme === 'swiss' 
    ? 'swiss-button swiss-button-primary' 
    : 'px-4 py-2 bg-warm-500 text-white hover:bg-warm-600 transition-colors text-sm font-medium';

  return (
    <div className={cn("min-h-screen", theme === 'swiss' ? 'swiss-bg-primary' : 'bg-sand-50')}>
      {/* Enhanced Global KPIs Header - Responsive */}
      <div className={cn("sticky top-16 z-40 px-4 lg:px-6 py-4", theme === 'swiss' ? 'swiss-bg-header' : 'bg-white border-b border-gray-200')}>
        <div className="flex justify-between items-center mb-4">
          <h1 className={cn(getTextClass('primary'), "text-xl lg:text-2xl font-bold wordmark")}>CLEAR Command Center</h1>
          <button 
            className={cn("px-3 py-2 text-sm font-medium transition-colors",
              theme === 'swiss' ? 'swiss-button-primary' : 'bg-orange-500 text-white hover:bg-orange-600'
            )}
            onClick={() => {/* Handle new crisis */}}
          >
            Report Crisis
          </button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 text-center">
          <div>
            <div className={cn(getTextClass('primary'), "text-lg lg:text-2xl font-bold", theme === 'swiss' ? 'swiss-text-accent-red' : 'text-red-600')}>
              {formatNumber(safeStats.activeCrises || 0)}
            </div>
            <div className={cn(getTextClass('secondary'), "text-xs lg:text-sm")}>Active Crises</div>
          </div>
          <div>
            <div className={cn(getTextClass('primary'), "text-lg lg:text-2xl font-bold", theme === 'swiss' ? 'swiss-text-accent-orange' : 'text-orange-600')}>
              {humanitarianUtils.formatPopulationCount(safeStats.affectedPopulation || 0)}
            </div>
            <div className={cn(getTextClass('secondary'), "text-xs lg:text-sm")}>People Affected</div>
          </div>
          <div>
            <div className={cn(getTextClass('primary'), "text-lg lg:text-2xl font-bold", theme === 'swiss' ? 'swiss-text-accent-blue' : 'text-blue-600')}>
              {safeStats.responsesActive || 0}
            </div>
            <div className={cn(getTextClass('secondary'), "text-xs lg:text-sm")}>Response Teams</div>
          </div>
          <div>
            <div className={cn(getTextClass('primary'), "text-lg lg:text-2xl font-bold", theme === 'swiss' ? 'swiss-text-accent-green' : 'text-green-600')}>
              2.1h
            </div>
            <div className={cn(getTextClass('secondary'), "text-xs lg:text-sm")}>Avg Response Time</div>
          </div>
        </div>
      </div>

      {error && (
        <div className={cn("mx-4 lg:mx-6 mt-4 p-4", 
          theme === 'swiss' ? 'swiss-border swiss-bg-warning' : 'border border-warm-200 bg-warm-50'
        )}>
          <p className={cn("text-sm", theme === 'swiss' ? 'swiss-text-warning' : 'text-warm-700')}>{error}</p>
        </div>
      )}

      {/* 3-Column Information-Rich Layout - Responsive */}
      <div className="dashboard-grid px-4 lg:px-6 py-6">
        {/* Column 1: Crisis Overview & Actions (30%) */}
        <div className="crisis-overview space-y-4">
          {/* Crisis Alert Stream */}
          <div className={cn(getCardClass(), "dashboard-card-lg")}>
            <div className={cn("px-4 py-3 flex items-center justify-between", theme === 'swiss' ? 'swiss-border-b' : 'border-b border-gray-200')}>
              <h2 className={cn(getTextClass('primary'), "text-lg font-semibold flex items-center")}>
                <SwissIcons.Emergency size={20} className="mr-2" />
                Crisis Alerts
              </h2>
              <span className={cn("text-xs px-2 py-1", theme === 'swiss' ? 'swiss-bg-warning' : 'bg-red-100 text-red-800')}>
                5 Active
              </span>
            </div>
            <div className="p-4 overflow-y-auto max-h-80">
              {[
                { title: "Earthquake M7.2 - Turkey/Syria", location: "Gaziantep Province", affected: "250K", time: "12 min ago", severity: "critical" },
                { title: "Flood Warning - Bangladesh", location: "Sylhet Division", affected: "180K", time: "28 min ago", severity: "high" },
                { title: "Drought Escalation - Somalia", location: "Bay Region", affected: "90K", time: "1h ago", severity: "medium" },
                { title: "Conflict Displacement - Ukraine", location: "Kharkiv Oblast", affected: "45K", time: "2h ago", severity: "high" },
                { title: "Cyclone Approach - Philippines", location: "Bicol Region", affected: "320K", time: "3h ago", severity: "critical" }
              ].map((alert, i) => (
                <div key={i} className={cn("p-3 mb-3 border-l-4 cursor-pointer hover:shadow-sm transition-shadow", 
                  alert.severity === 'critical' ? (theme === 'swiss' ? 'swiss-border-emergency swiss-bg-subtle' : 'border-red-500 bg-red-50') :
                  alert.severity === 'high' ? (theme === 'swiss' ? 'swiss-border-warning swiss-bg-subtle' : 'border-orange-500 bg-orange-50') :
                  (theme === 'swiss' ? 'swiss-border-info swiss-bg-subtle' : 'border-yellow-500 bg-yellow-50')
                )}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className={cn(getTextClass('primary'), "font-medium text-sm")}>
                        {alert.title}
                      </h4>
                      <p className={cn(getTextClass('secondary'), "text-xs mt-1")}>
                        📍 {alert.location} • 👥 {alert.affected} at risk • ⏱️ {alert.time}
                      </p>
                    </div>
                    <div className="flex gap-1 ml-2">
                      <button className={cn("px-2 py-1 text-xs font-medium", theme === 'swiss' ? 'swiss-button-primary' : 'bg-blue-500 text-white')}>
                        Assess
                      </button>
                      <button className={cn("px-2 py-1 text-xs font-medium", theme === 'swiss' ? 'swiss-button-danger' : 'bg-red-500 text-white')}>
                        Deploy
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Active Crisis Cards */}
          <div className={cn(getCardClass(), "dashboard-card-lg")}>
            <div className={cn("px-4 py-3", theme === 'swiss' ? 'swiss-border-b' : 'border-b border-gray-200')}>
              <h2 className={cn(getTextClass('primary'), "text-lg font-semibold flex items-center")}>
                <SwissIcons.TrendAnalysis size={20} className="mr-2" />
                Active Crises
              </h2>
            </div>
            <div className="p-4 overflow-y-auto max-h-80">
              {crises.map((crisis) => (
                <div key={crisis.id} className={cn("p-4 mb-3 cursor-pointer hover:shadow-md transition-shadow", getCardClass())} 
                     onClick={() => onCrisisSelect({...crisis, confidence: 0.85, evidence: [], coordinates: [0, 0]})}>
                  <div className="flex justify-between items-start mb-2">
                    <h4 className={cn(getTextClass('primary'), "font-semibold text-sm")}>{crisis.title}</h4>
                    <span className={cn("px-2 py-1 text-xs font-medium", getSeverityColorClass(crisis.severity))}>
                      L{crisis.severity}
                    </span>
                  </div>
                  <div className="text-xs space-y-1 mb-3">
                    <div className="flex justify-between">
                      <span className={getTextClass('secondary')}>📍 {crisis.location}</span>
                      <span className={getTextClass('secondary')}>👥 {humanitarianUtils.formatPopulationCount(crisis.affected_population)}</span>
                    </div>
                    <div className="w-full bg-gray-200 h-2">
                      <div className={cn("h-2", theme === 'swiss' ? 'swiss-bg-accent' : 'bg-blue-500')} 
                           style={{ width: `${65 + (crisis.id * 7) % 30}%` }}></div>
                    </div>
                    <span className={cn(getTextClass('secondary'), "text-xs")}>Response Progress: {65 + (crisis.id * 7) % 30}%</span>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={(e) => { e.stopPropagation(); onMakeDecision({...crisis, id: crisis.id.toString(), description: `Crisis in ${crisis.location}`, evidence: [], confidence: 85}); }}
                      className={cn("px-2 py-1 text-xs font-medium", theme === 'swiss' ? 'swiss-button-danger' : 'bg-red-500 text-white')}>
                      Decide
                    </button>
                    <button className={cn("px-2 py-1 text-xs font-medium", theme === 'swiss' ? 'swiss-button-secondary' : 'bg-gray-500 text-white')}>
                      Update
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Urgent Actions Panel */}
          <div className={cn(getCardClass(), "dashboard-card-sm")}>
            <div className={cn("px-4 py-3", theme === 'swiss' ? 'swiss-border-b' : 'border-b border-gray-200')}>
              <h3 className={cn(getTextClass('primary'), "text-lg font-semibold flex items-center")}>
                <SwissIcons.Clock size={20} className="mr-2" />
                Urgent Actions
              </h3>
            </div>
            <div className="p-4 space-y-2 overflow-y-auto max-h-40">
              {[
                { action: "Approve $2M emergency funding", deadline: "2h", priority: "high" },
                { action: "Deploy medical team to Gaza", deadline: "6h", priority: "high" },
                { action: "Coordinate with WHO on vaccine supply", deadline: "1d", priority: "medium" }
              ].map((item, i) => (
                <div key={i} className={cn("p-2 flex justify-between items-center", 
                  theme === 'swiss' ? 'swiss-bg-subtle' : 'bg-yellow-50')}>
                  <span className={cn(getTextClass('primary'), "text-sm flex-1 mr-2")}>{item.action}</span>
                  <div className="flex items-center gap-2">
                    <span className={cn("text-xs", item.priority === 'high' ? 'text-red-600' : 'text-orange-600')}>
                      {item.deadline}
                    </span>
                    <button className={cn("px-2 py-1 text-xs", theme === 'swiss' ? 'swiss-button-primary' : 'bg-blue-500 text-white')}>
                      Act
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Column 2: Situational Awareness & Intelligence (40%) */}
        <div className="situational-awareness space-y-4">
          {/* Interactive Global Map */}
          <div className={cn(getCardClass(), "dashboard-card-lg")}>
            <div className={cn("px-4 py-3 flex justify-between items-center", theme === 'swiss' ? 'swiss-border-b' : 'border-b border-gray-200')}>
              <h2 className={cn(getTextClass('primary'), "text-lg font-semibold flex items-center")}>
                <SwissIcons.Globe size={20} className="mr-2" />
                Global Situation Map
              </h2>
              <div className="flex gap-2">
                <button className={cn("px-2 py-1 text-xs", theme === 'swiss' ? 'swiss-button-secondary' : 'bg-gray-200 text-gray-700')}>
                  Population
                </button>
                <button className={cn("px-2 py-1 text-xs", theme === 'swiss' ? 'swiss-button-secondary' : 'bg-gray-200 text-gray-700')}>
                  Weather
                </button>
                <button className={cn("px-2 py-1 text-xs", theme === 'swiss' ? 'swiss-button-primary' : 'bg-blue-500 text-white')}>
                  Crises
                </button>
              </div>
            </div>
            <div className="p-4 h-80">
              <DashboardMap 
                crises={crises.map(crisis => ({
                  ...crisis,
                  confidence: 0.85,
                  evidence: [],
                  coordinates: [0, 0]
                }))} 
                onCrisisSelect={onCrisisSelect} 
              />
            </div>
          </div>

          {/* Real-Time Analytics Dashboard */}
          <div className={cn(getCardClass(), "dashboard-card-md")}>
            <div className={cn("px-4 py-3", theme === 'swiss' ? 'swiss-border-b' : 'border-b border-gray-200')}>
              <h3 className={cn(getTextClass('primary'), "text-lg font-semibold flex items-center")}>
                <SwissIcons.Analytics size={20} className="mr-2" />
                Real-Time Analytics
              </h3>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center">
                  <div className={cn(getTextClass('primary'), "text-xl font-bold")}>847K</div>
                  <div className={getTextClass('secondary')}>People at Risk</div>
                </div>
                <div className="text-center">
                  <div className={cn(getTextClass('primary'), "text-xl font-bold")}>96%</div>
                  <div className={getTextClass('secondary')}>Resource Efficiency</div>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className={getTextClass('secondary')}>Crisis Response Rate</span>
                    <span className={getTextClass('primary')}>78%</span>
                  </div>
                  <div className="w-full bg-gray-200 h-2">
                    <div className={cn("h-2", theme === 'swiss' ? 'swiss-bg-accent' : 'bg-green-500')} 
                         style={{ width: '78%' }}></div>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className={getTextClass('secondary')}>Population Reached</span>
                    <span className={getTextClass('primary')}>412K / 530K</span>
                  </div>
                  <div className="w-full bg-gray-200 h-2">
                    <div className={cn("h-2", theme === 'swiss' ? 'swiss-bg-warning' : 'bg-orange-500')} 
                         style={{ width: '77%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* AI Insights & Recommendations */}
          <div className={cn(getCardClass(), "dashboard-card-md")}>
            <div className={cn("px-4 py-3", theme === 'swiss' ? 'swiss-border-b' : 'border-b border-gray-200')}>
              <h3 className={cn(getTextClass('primary'), "text-lg font-semibold flex items-center")}>
                <SwissIcons.AISystem size={20} className="mr-2" />
                AI Insights
              </h3>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto max-h-48">
              <div className={cn("p-3 border-l-4", theme === 'swiss' ? 'swiss-border-info swiss-bg-subtle' : 'border-blue-500 bg-blue-50')}>
                <h4 className={cn(getTextClass('primary'), "font-medium text-sm")}>Resource Optimization</h4>
                <p className={cn(getTextClass('secondary'), "text-xs mt-1")}>
                  Redistribute 40% medical supplies from stable regions to earthquake zones for 23% efficiency gain.
                </p>
              </div>
              <div className={cn("p-3 border-l-4", theme === 'swiss' ? 'swiss-border-warning swiss-bg-subtle' : 'border-yellow-500 bg-yellow-50')}>
                <h4 className={cn(getTextClass('primary'), "font-medium text-sm")}>Early Warning</h4>
                <p className={cn(getTextClass('secondary'), "text-xs mt-1")}>
                  Drought conditions detected in East Africa. 85% probability of crisis escalation in 14 days.
                </p>
              </div>
              <div className={cn("p-3 border-l-4", theme === 'swiss' ? 'swiss-border-success swiss-bg-subtle' : 'border-green-500 bg-green-50')}>
                <h4 className={cn(getTextClass('primary'), "font-medium text-sm")}>Success Pattern</h4>
                <p className={cn(getTextClass('secondary'), "text-xs mt-1")}>
                  Inter-agency coordination model from Haiti response shows 31% better outcomes. Apply to current operations?
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Column 3: Operations & Intelligence (30%) */}
        <div className="operations-intelligence space-y-4">
          {/* Resource Management Panel */}
          <div className={cn(getCardClass(), "dashboard-card-md")}>
            <div className={cn("px-4 py-3", theme === 'swiss' ? 'swiss-border-b' : 'border-b border-gray-200')}>
              <h3 className={cn(getTextClass('primary'), "text-lg font-semibold flex items-center")}>
                <SwissIcons.Package size={20} className="mr-2" />
                Resource Status
              </h3>
            </div>
            <div className="p-4 space-y-3 overflow-y-auto max-h-64">
              {[
                { type: "Medical Supplies", available: 850, deployed: 340, status: "adequate", utilization: 40 },
                { type: "Food Aid", available: 2400, deployed: 1890, status: "low", utilization: 79 },
                { type: "Shelter Kits", available: 450, deployed: 380, status: "critical", utilization: 84 },
                { type: "Personnel", available: 125, deployed: 98, status: "adequate", utilization: 78 }
              ].map((resource, i) => (
                <div key={i} className={cn("p-3", theme === 'swiss' ? 'swiss-bg-subtle' : 'bg-gray-50')}>
                  <div className="flex justify-between items-center mb-2">
                    <span className={cn(getTextClass('primary'), "font-medium text-sm")}>{resource.type}</span>
                    <span className={cn("px-2 py-1 text-xs font-medium", getResourceStatusColor(resource.status))}>
                      {resource.status}
                    </span>
                  </div>
                  <div className="text-xs space-y-1">
                    <div className="flex justify-between">
                      <span className={getTextClass('secondary')}>Available: {resource.available}</span>
                      <span className={getTextClass('secondary')}>Deployed: {resource.deployed}</span>
                    </div>
                    <div className="w-full bg-gray-200 h-1.5">
                      <div className={cn("h-1.5", 
                        resource.status === 'critical' ? 'bg-red-500' : 
                        resource.status === 'low' ? 'bg-yellow-500' : 'bg-green-500'
                      )} style={{ width: `${resource.utilization}%` }}></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Communication Hub */}
          <div className={cn(getCardClass(), "dashboard-card-md")}>
            <div className={cn("px-4 py-3", theme === 'swiss' ? 'swiss-border-b' : 'border-b border-gray-200')}>
              <h3 className={cn(getTextClass('primary'), "text-lg font-semibold flex items-center")}>
                <SwissIcons.Message size={20} className="mr-2" />
                Field Updates
              </h3>
            </div>
            <div className="p-4 overflow-y-auto max-h-64 space-y-3">
              {[
                { time: "2m ago", from: "Field Team Alpha", message: "Distribution complete at camp 7. 450 families reached.", type: "success" },
                { time: "8m ago", from: "WHO Coordinator", message: "Vaccination program delayed due to security concerns.", type: "warning" },
                { time: "15m ago", from: "Logistics Hub", message: "Supply convoy departed. ETA 3 hours to affected zone.", type: "info" },
                { time: "23m ago", from: "Local Partner", message: "Community leader requests meeting on relocation plans.", type: "info" }
              ].map((update, i) => (
                <div key={i} className={cn("p-3 border-l-4", 
                  update.type === 'success' ? (theme === 'swiss' ? 'swiss-border-success swiss-bg-subtle' : 'border-green-500 bg-green-50') :
                  update.type === 'warning' ? (theme === 'swiss' ? 'swiss-border-warning swiss-bg-subtle' : 'border-yellow-500 bg-yellow-50') :
                  (theme === 'swiss' ? 'swiss-border-info swiss-bg-subtle' : 'border-blue-500 bg-blue-50')
                )}>
                  <div className="flex justify-between items-start mb-1">
                    <span className={cn(getTextClass('primary'), "font-medium text-xs")}>{update.from}</span>
                    <span className={cn(getTextClass('secondary'), "text-xs")}>{update.time}</span>
                  </div>
                  <p className={cn(getTextClass('secondary'), "text-xs")}>{update.message}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Performance Metrics */}
          <div className={cn(getCardClass(), "dashboard-card-sm")}>
            <div className={cn("px-4 py-3", theme === 'swiss' ? 'swiss-border-b' : 'border-b border-gray-200')}>
              <h3 className={cn(getTextClass('primary'), "text-lg font-semibold flex items-center")}>
                <SwissIcons.TrendAnalysis size={20} className="mr-2" />
                Performance
              </h3>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="text-center">
                  <div className={cn(getTextClass('primary'), "text-xl font-bold")}>2.1h</div>
                  <div className={getTextClass('secondary')}>Avg Response</div>
                </div>
                <div className="text-center">
                  <div className={cn(getTextClass('primary'), "text-xl font-bold")}>94%</div>
                  <div className={getTextClass('secondary')}>Decision Accuracy</div>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className={getTextClass('secondary')}>People Reached:</span>
                  <span className={getTextClass('primary')}>412,847</span>
                </div>
                <div className="flex justify-between">
                  <span className={getTextClass('secondary')}>Cost per Person:</span>
                  <span className={getTextClass('primary')}>$23.50</span>
                </div>
                <div className="flex justify-between">
                  <span className={getTextClass('secondary')}>Active Responses:</span>
                  <span className={getTextClass('primary')}>{safeStats.responsesActive || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className={getTextClass('secondary')}>Decisions Today:</span>
                  <span className={getTextClass('primary')}>47</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;